import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { NAVIGATION_CONFIG } from '../../config/NavigationConfig';
import { ChevronDown, ChevronRight, LogOut } from 'lucide-react';

const Sidebar: React.FC = () => {
	const location = useLocation();
	const { logout, user } = useAuth();
	const path = location.pathname;

	// State for collapsed groups
	const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

	const toggleGroup = (title: string) => {
		setCollapsedGroups(prev => ({
			...prev,
			[title]: !prev[title]
		}));
	};

	const isActive = (route: string) => {
		if (route === '/' && path === '/') return true;
		if (route !== '/' && path.startsWith(route)) return true;
		return false;
	};

	// Dynamic RBAC Filter: Prune groups and items requiring roles not held by currentUser
	const visibleGroups = NAVIGATION_CONFIG.filter((group) => {
		if (group.requiredRoles && (!user?.role || !group.requiredRoles.includes(user.role))) {
			return false;
		}
		return true;
	}).map((group) => ({
		...group,
		items: group.items.filter((item) => {
			if (item.requiredRoles && (!user?.role || !item.requiredRoles.includes(user.role))) {
				return false;
			}
			return true;
		})
	})).filter(group => group.items.length > 0);

	return (
		<aside className="w-full h-full bg-slate-900 border-r border-slate-800 flex flex-col text-slate-300">
			{/* Header */}
			<div className="h-16 flex items-center px-6 border-b border-slate-800 shrink-0">
				<div className="flex items-center gap-3">
					<div className="h-8 w-8 bg-sky-600 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-sky-900/20">
						G
					</div>
					<h1 className="text-lg font-bold tracking-tight text-white group cursor-default">
						GFF <span className="text-sky-500 font-light">Cockpit</span>
					</h1>
				</div>
			</div>

			{/* Navigation */}
			<nav className="flex-1 overflow-y-auto py-4 space-y-1 custom-scrollbar min-h-0">
				{visibleGroups.map((group) => {
					const isCollapsed = collapsedGroups[group.title];

					return (
						<div key={group.title} className="mb-2">
							{/* Group Header */}
							<button
								onClick={() => toggleGroup(group.title)}
								className="w-full flex items-center justify-between px-6 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors"
							>
								<span>{group.title}</span>
								{isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
							</button>

							{/* Group Items */}
							{!isCollapsed && (
								<div className="space-y-0.5 mt-1">
									{group.items.map((item) => {
										const active = isActive(item.route);
										const Icon = item.icon;

										return (
											<Link
												key={item.route}
												to={item.route}
												className={`flex items-center gap-3 px-6 py-2 text-sm font-medium transition-all duration-200 border-l-2 ${active
													? 'bg-slate-800/50 text-sky-400 border-sky-400'
													: 'text-slate-400 border-transparent hover:text-slate-200 hover:bg-slate-800/30'
													}`}
											>
												{Icon && <Icon size={18} />}
												<span>{item.label}</span>
												{item.badge && (
													<span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded ${item.badge === 'NEW'
														? 'bg-emerald-500/20 text-emerald-400'
														: 'bg-sky-500/20 text-sky-400'
														}`}>
														{item.badge}
													</span>
												)}
											</Link>
										);
									})}
								</div>
							)}
						</div>
					);
				})}
			</nav>

			{/* Footer */}
			<div className="p-4 border-t border-slate-800 bg-slate-900/50 shrink-0">
				<button
					onClick={logout}
					className="w-full py-2 px-4 rounded-md bg-rose-500/10 text-rose-400 border border-rose-500/20 hover:bg-rose-500/20 hover:border-rose-500/30 transition-all text-sm font-medium flex items-center justify-center gap-2"
				>
					<LogOut size={16} />
					<span>Logout</span>
				</button>
			</div>
		</aside>
	);
};

export default Sidebar;
